import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Beaker, Shield, Truck, FlaskConical, Microscope, Atom } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import OffersStrip from "@/components/OffersStrip";

const LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_e55312d9-2654-4e67-82f7-261766d13168/artifacts/8b215ca487d0b10f_IMG-20260910-WA0015.jpg";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [cats, setCats] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setFeatured(r.data.slice(0, 6)));
    api.get("/products/categories").then((r) => setCats(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b-[3px] border-black">
        <div className="hex-grid absolute inset-0 opacity-70" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-16 pb-20 md:pt-24 md:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border-[3px] border-black bg-[#facc15] px-3 py-1.5 font-mono-ui text-[10px] font-bold mb-6">
              <span className="w-2 h-2 bg-black" /> RESEARCH USE ONLY · UK LAB SUPPLY
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
              RESEARCH-GRADE
              <br />
              <span className="bg-[#ff1493] text-white px-3 inline-block border-[4px] border-black shadow-brut mt-2">
                PEPTIDES
              </span>
              <br />
              DELIVERED <span className="text-[#00d4ff] bg-black px-3 inline-block border-[4px] border-black">UK-WIDE</span>
            </h1>
            <p className="mt-6 text-base md:text-lg max-w-xl text-black/80">
              Independently verified compounds, courier-tracked despatch and strict in-vitro use only. Built for serious research &mdash; no fluff, no hype.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="inline-flex items-center gap-2 px-6 py-4 bg-[#ff1493] text-white border-[3px] border-black shadow-brut btn-press font-mono-ui text-xs font-bold"
              >
                BROWSE CATALOG <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/research-use"
                data-testid="hero-disclaimer-btn"
                className="inline-flex items-center gap-2 px-6 py-4 bg-white border-[3px] border-black shadow-brut btn-press font-mono-ui text-xs font-bold"
              >
                RESEARCH DISCLAIMER
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              {[
                { k: "20+", v: "Compounds" },
                { k: "≥99%", v: "Avg. purity" },
                { k: "48h", v: "UK dispatch" },
              ].map((s) => (
                <div key={s.v} className="border-[3px] border-black bg-white p-3">
                  <div className="font-display text-2xl text-[#ff1493]">{s.k}</div>
                  <div className="font-mono-ui text-[10px] text-black/60">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-full h-full bg-[#00d4ff] border-[4px] border-black" />
              <div className="relative bg-white border-[4px] border-black p-5 shadow-brut">
                <img src={LOGO} alt="UK Peptide Research" className="w-full h-auto object-contain" />
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <div className="font-mono-ui text-[10px] p-2 border-[2px] border-black text-center">LOT ·0241</div>
                  <div className="font-mono-ui text-[10px] p-2 border-[2px] border-black text-center bg-[#ff1493] text-white">PURITY 99.6%</div>
                  <div className="font-mono-ui text-[10px] p-2 border-[2px] border-black text-center">HPLC·MS</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SPECIAL OFFERS */}
      <OffersStrip />

      {/* FEATURE STRIP */}
      <section className="border-b-[3px] border-black bg-black text-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3">
          {[
            { Icon: Shield, title: "Third-party tested", body: "Every batch verified via HPLC & mass spec" },
            { Icon: Truck, title: "Shipped from the UK", body: "Discreet packaging · tracked 24/48h courier" },
            { Icon: Beaker, title: "Research-grade only", body: "Strictly for in-vitro laboratory use" },
          ].map(({ Icon, title, body }, i) => (
            <div key={title} className={`p-6 md:p-8 flex items-start gap-4 ${i < 2 ? "md:border-r border-white/20" : ""} ${i > 0 ? "border-t md:border-t-0 border-white/20" : ""}`}>
              <div className="w-12 h-12 bg-[#ff1493] border-[3px] border-white flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display text-lg">{title}</div>
                <div className="font-mono-ui text-[11px] text-white/70 mt-1">{body}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="border-b-[3px] border-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <div className="font-mono-ui text-xs text-[#ff1493] mb-2">— CATEGORIES</div>
              <h2 className="font-display text-4xl md:text-5xl leading-none">Explore the catalog</h2>
            </div>
            <Link to="/shop" className="font-mono-ui text-xs font-bold inline-flex items-center gap-1 hover:text-[#ff1493]">
              VIEW ALL <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {cats.slice(0, 10).map((c, i) => {
              const Icon = [FlaskConical, Microscope, Beaker, Atom, Shield][i % 5];
              const bg = ["bg-[#ff1493] text-white", "bg-[#00d4ff] text-black", "bg-white", "bg-black text-white", "bg-[#facc15]"][i % 5];
              return (
                <Link
                  key={c}
                  to={`/shop?category=${encodeURIComponent(c)}`}
                  className={`${bg} border-[3px] border-black p-5 shadow-brut-sm btn-press group`}
                >
                  <Icon className="w-7 h-7 mb-3" strokeWidth={2.5} />
                  <div className="font-display text-sm leading-tight">{c}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="border-b-[3px] border-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <div className="font-mono-ui text-xs text-[#00d4ff] mb-2">— TOP PICKS</div>
              <h2 className="font-display text-4xl md:text-5xl leading-none">Featured compounds</h2>
            </div>
            <Link to="/shop" data-testid="home-view-all" className="font-mono-ui text-xs font-bold inline-flex items-center gap-1 hover:text-[#ff1493]">
              SHOP EVERYTHING <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* DISCLAIMER CTA */}
      <section className="bg-[#facc15] border-b-[3px] border-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-display text-3xl md:text-4xl">For research use only.</div>
            <p className="mt-2 font-mono-ui text-xs max-w-2xl">
              Our compounds are supplied strictly for in-vitro laboratory research. They are not intended for human or animal consumption, diagnosis or treatment.
            </p>
          </div>
          <Link to="/research-use" className="shrink-0 inline-flex items-center gap-2 px-6 py-4 bg-black text-white border-[3px] border-black shadow-brut btn-press font-mono-ui text-xs font-bold">
            FULL DISCLAIMER <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
